'use client';

import React from 'react';
import { XMarkIcon, ExclamationTriangleIcon, FolderIcon, DocumentIcon } from '@heroicons/react/24/outline';

interface DeleteConfirmModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    objectName: string;
    isFolder: boolean;
}

export default function DeleteConfirmModal({ 
    isOpen, 
    onClose, 
    onConfirm, 
    objectName, 
    isFolder 
}: DeleteConfirmModalProps) {
    if (!isOpen) return null;

    const handleConfirm = () => {
        onConfirm();
    };

    return (
        <div className="fixed inset-0 bg-[#00000099] flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
                        Xác nhận xóa
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                                <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
                            </div>
                        </div>
                        
                        <div className="flex-1">
                            <p className="text-sm text-gray-700 mb-4">
                                Bạn có chắc chắn muốn xóa {isFolder ? 'folder' : 'file'}:
                            </p>
                            
                            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg mb-4">
                                {isFolder ? (
                                    <FolderIcon className="w-5 h-5 text-blue-500 flex-shrink-0" />
                                ) : (
                                    <DocumentIcon className="w-5 h-5 text-gray-500 flex-shrink-0" />
                                )}
                                <span className="font-medium text-gray-900 break-all">
                                    {objectName}
                                </span>
                            </div>

                            {isFolder && (
                                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg mb-4">
                                    <p className="text-sm text-yellow-800">
                                        <strong>Cảnh báo:</strong> Xóa folder sẽ xóa tất cả file và folder con bên trong. 
                                        Hành động này không thể hoàn tác.
                                    </p>
                                </div>
                            )}

                            <p className="text-sm text-gray-600">
                                Hành động này không thể hoàn tác. Bạn có chắc chắn muốn tiếp tục?
                            </p>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 p-4 border-t bg-gray-50">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                        Hủy
                    </button>
                    <button
                        onClick={handleConfirm}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                        Xóa {isFolder ? 'Folder' : 'File'}
                    </button>
                </div>
            </div>
        </div>
    );
}
